<script setup>
    import { ref, onMounted, defineProps, defineExpose, defineEmits, onUnmounted } from 'vue';
    
    const props = defineProps({
        applicationUrl: {
            type: String,
            required: true
        },
        allowCommunication: {
            type: Boolean,
            default: true
        },
        sandboxOptions: {
            type: String,
            default: "allow-scripts allow-same-origin"
        }
    });

    const applicationFrame = ref(null);
    const messageFromApp = ref(null);

    // Expose methods to parent components
    const sendMessageToApp = (message) => {
        if (applicationFrame.value && applicationFrame.value.contentWindow) {
            applicationFrame.value.contentWindow.postMessage(message, window.location.origin);
        }
    };

    // Set up communication between Vue and iframe if needed
    onMounted(() => {
        if (props.allowCommunication) {
            window.addEventListener('message', handleMessage);
        }
    });

    const handleMessage = (event) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) return;
        
        // Handle messages from the iframe
        // console.log('Message from application:', event.data);
        messageFromApp.value = event.data;
        
        // Emit the message to parent components
        emit('message', event.data);
    };

    // Clean up event listeners when component is unmounted
    onUnmounted(() => {
        if (props.allowCommunication) {
            window.removeEventListener('message', handleMessage);
        }
    });

    // Expose methods and data to parent components
    defineExpose({
        sendMessageToApp,
        messageFromApp,
        applicationFrame
    });

    // Emit events
    const emit = defineEmits(['message', 'load', 'error']);
</script>

<template>
    <div class="embedded-application w-full h-full min-h-[500px] relative overflow-hidden bg-gray-50 rounded-lg shadow-md">
        <iframe
            :src="applicationUrl"
            :sandbox="sandboxOptions"
            width="100%"
            height="100%"
            ref="applicationFrame"
            @load="emit('load', $event)"
            @error="emit('error', $event)"
        ></iframe>
    </div>
</template>